import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import { Button, Card, FormField, Textarea, useToast } from "@chotca/ui";
import { useMe, useBulkCreateOrgAddresses } from "@chotca/api-client";
import { useAuthStore } from "@chotca/auth";

// Bulk-add branches by pasting Google Maps links (SCRUM-73, spec §5.5). One link
// per line; the server geocodes each and reports which resolved / failed.
export function BulkBranchesClient() {
  const nav = useNavigate();
  const { toast } = useToast();
  const me = useMe();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const orgId = activeOrgId ?? me.data?.memberships[0]?.orgId ?? "";
  const bulk = useBulkCreateOrgAddresses(orgId);

  const [text, setText] = React.useState("");
  const [failed, setFailed] = React.useState<
    { mapUrl: string; error: string }[]
  >([]);

  const links = text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const submit = async () => {
    setFailed([]);
    try {
      const res = await bulk.mutateAsync({
        items: links.map((mapUrl) => ({ mapUrl })),
      });
      if (res.created.length > 0) {
        toast({
          tone: "success",
          title: `Đã thêm ${res.created.length} chi nhánh`,
        });
      }
      if (res.failed.length > 0) {
        setFailed(res.failed);
        // Keep only the failed lines so the user can fix and retry.
        setText(res.failed.map((f) => f.mapUrl).join("\n"));
      } else {
        nav("/org");
      }
    } catch (e) {
      toast({
        tone: "error",
        title: e instanceof Error ? e.message : "Thêm chi nhánh thất bại",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Link
        to="/org"
        className="inline-flex items-center gap-1.5 text-body-sm font-semibold text-ink/70 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Quay lại
      </Link>

      <header>
        <h1 className="text-display-lg font-extrabold text-ink">
          Thêm chi nhánh hàng loạt
        </h1>
        <p className="mt-1 max-w-xl text-body text-ash">
          Dán nhiều link Google Maps, mỗi dòng một chi nhánh. Hệ thống tự lấy
          địa chỉ và toạ độ từ link.
        </p>
      </header>

      <Card className="space-y-4 p-6">
        <FormField
          label="Link Google Maps"
          description="Mỗi dòng một link. Tối đa 20 dòng."
        >
          <Textarea
            rows={8}
            placeholder={
              "https://maps.app.goo.gl/...\nhttps://maps.app.goo.gl/..."
            }
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </FormField>

        {failed.length > 0 ? (
          <div className="space-y-1 rounded-md border-l-4 border-danger bg-danger/5 p-3">
            <p className="text-body-sm font-bold text-danger">
              {failed.length} link không thêm được — đã giữ lại để bạn sửa:
            </p>
            {failed.map((f) => (
              <p
                key={f.mapUrl}
                className="flex items-center gap-1.5 text-xs text-ink/70"
              >
                <XCircle size={12} className="shrink-0 text-danger" />
                {f.error}
              </p>
            ))}
          </div>
        ) : null}

        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-body-sm text-ash">
            <CheckCircle2 size={14} />
            {links.length} link
          </span>
          <Button
            onClick={submit}
            loading={bulk.isPending}
            disabled={links.length === 0}
            type="button"
          >
            Thêm {links.length > 0 ? links.length : ""} chi nhánh
          </Button>
        </div>
      </Card>
    </div>
  );
}
